import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useResource } from "../lib/hooks";
import { PageHeader, Pill } from "../components/Common";
import { FunnelRenderer } from "../components/FunnelRenderer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const BLOCKS = ["hero", "text", "image", "cta", "form"];
const NEW_BLOCK = { hero: { heading: "Your headline", text: "Supporting sub-headline", button_text: "Get started" }, text: { heading: "Section", text: "Write something…" },
  image: { image: "" }, cta: { heading: "Ready to start?", button_text: "Book now" }, form: { heading: "Get in touch" } };

function BlockEditor({ b, forms, onChange, onMove, onDelete, i }) {
  const set = (k, v) => onChange({ ...b, [k]: v });
  return (
    <div className="rounded border p-3 space-y-2 bg-background" data-testid={`funnel-block-${i}`}>
      <div className="flex items-center gap-2"><Pill tone="teal">{b.type}</Pill>
        <div className="ml-auto flex"><Button size="icon" variant="ghost" onClick={() => onMove(-1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" onClick={() => onMove(1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" onClick={onDelete}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></div>
      </div>
      {b.type !== "image" && <Input placeholder="Heading" value={b.heading || ""} onChange={(e) => set("heading", e.target.value)} />}
      {["hero", "text", "cta"].includes(b.type) && <Textarea rows={2} placeholder="Text" value={b.text || ""} onChange={(e) => set("text", e.target.value)} />}
      {["hero", "image"].includes(b.type) && <Input placeholder="Image URL" value={b.image || ""} onChange={(e) => set("image", e.target.value)} />}
      {["hero", "cta"].includes(b.type) && <div className="grid grid-cols-2 gap-2"><Input placeholder="Button text" value={b.button_text || ""} onChange={(e) => set("button_text", e.target.value)} /><Input placeholder="Button link" value={b.button_link || ""} onChange={(e) => set("button_link", e.target.value)} /></div>}
      {b.type === "form" && (
        <select className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={b.form_id || ""} onChange={(e) => set("form_id", e.target.value)}>
          <option value="">Choose form…</option>{forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      )}
    </div>
  );
}

function Builder({ funnel, forms, onSave, onClose }) {
  const [f, setF] = useState({ published: false, steps: [{ name: "Landing", blocks: [{ type: "hero", ...NEW_BLOCK.hero }] }], ...funnel });
  const [si, setSi] = useState(0);
  const step = f.steps[si] || f.steps[0];
  const setStep = (s) => setF((p) => ({ ...p, steps: p.steps.map((x, j) => (j === si ? s : x)) }));
  const setBlocks = (blocks) => setStep({ ...step, blocks });
  const move = (i, d) => { const b = [...step.blocks]; const j = i + d; if (j < 0 || j >= b.length) return; [b[i], b[j]] = [b[j], b[i]]; setBlocks(b); };
  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4" data-testid="funnel-builder">
      <div className="xl:col-span-5 space-y-4">
        <div className="rounded-md border bg-card p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Funnel name</Label><Input data-testid="funnel-name-input" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div className="space-y-1"><Label>URL slug</Label><Input data-testid="funnel-slug-input" value={f.slug || ""} onChange={(e) => setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} /></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="funnel-publish-switch" />Published</label>
          <div className="overline">Steps</div>
          <div className="flex flex-wrap gap-2">
            {f.steps.map((s, i) => <Button key={i} size="sm" variant={i === si ? "default" : "secondary"} onClick={() => setSi(i)} data-testid={`funnel-step-${i}`}>{i + 1}. {s.name}</Button>)}
            <Button size="sm" variant="outline" onClick={() => { setF({ ...f, steps: [...f.steps, { name: `Step ${f.steps.length + 1}`, blocks: [] }] }); setSi(f.steps.length); }} data-testid="add-funnel-step-btn"><Plus className="h-3.5 w-3.5" /></Button>
          </div>
          <Input value={step.name} onChange={(e) => setStep({ ...step, name: e.target.value })} placeholder="Step name" />
        </div>
        <div className="rounded-md border bg-card p-4 space-y-3">
          <div className="flex flex-wrap gap-2">{BLOCKS.map((t) => <Button key={t} size="sm" variant="secondary" onClick={() => setBlocks([...step.blocks, { type: t, ...NEW_BLOCK[t] }])} data-testid={`add-block-${t}`}>+ {t}</Button>)}</div>
          {step.blocks.map((b, i) => <BlockEditor key={i} i={i} b={b} forms={forms} onChange={(nb) => setBlocks(step.blocks.map((x, j) => (j === i ? nb : x)))} onMove={(d) => move(i, d)} onDelete={() => setBlocks(step.blocks.filter((_, j) => j !== i))} />)}
        </div>
        <div className="flex gap-2"><Button variant="ghost" onClick={onClose}>Close</Button>
          <Button className="ml-auto" onClick={() => { if (!f.name || !f.slug) return toast.error("Name and slug required"); onSave(f); }} data-testid="save-funnel-btn">Save funnel</Button></div>
      </div>
      <div className="xl:col-span-7 rounded-md border overflow-hidden h-[75vh] overflow-y-auto" data-testid="funnel-preview"><FunnelRenderer step={step} /></div>
    </div>
  );
}

export default function Funnels() {
  const funnels = useResource("funnels");
  const forms = useResource("forms");
  const [edit, setEdit] = useState(null);
  return (
    <div>
      <PageHeader overline="Growth" title="Funnel & Landing Builder" desc="Compose multi-step funnels from blocks, embed forms and publish to a public URL.">
        {!edit && <Button onClick={() => setEdit({ name: "", slug: "" })} data-testid="new-funnel-btn"><Plus className="h-4 w-4 mr-1" />New funnel</Button>}
      </PageHeader>
      {edit ? <Builder funnel={edit} forms={forms.items} onClose={() => setEdit(null)} onSave={async (f) => { await funnels.save(f, f.id); toast.success("Funnel saved"); setEdit(null); }} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {!funnels.items.length && <div className="rounded-md border p-8 text-muted-foreground md:col-span-3 text-center">No funnels yet.</div>}
          {funnels.items.map((f) => (
            <div key={f.id} className="rounded-md border bg-card p-5 space-y-3" data-testid={`funnel-card-${f.id}`}>
              <div className="flex justify-between"><div className="font-display text-lg font-bold">{f.name}</div><Pill tone={f.published ? "teal" : "slate"}>{f.published ? "published" : "draft"}</Pill></div>
              <div className="text-xs font-mono text-muted-foreground">/p/{f.slug} · {(f.steps || []).length} steps</div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setEdit(f)} data-testid={`edit-funnel-${f.id}`}>Edit</Button>
                {f.published && <a href={`/p/${f.slug}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" data-testid={`open-funnel-${f.id}`}><ExternalLink className="h-3.5 w-3.5" /></Button></a>}
                <Button size="sm" variant="ghost" onClick={() => window.confirm("Delete funnel?") && funnels.remove(f.id)} data-testid={`delete-funnel-${f.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
